import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../../services/api';
import ImageUploader from '../../components/ImageUploader';
import { Input, TextArea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { getImageUrl } from '../../utils/imageUtils';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SectionHeader = styled.h3`
  margin: 2rem 0 1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }
`;

const HeroImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const Thumbnail = styled.div`
  position: relative;
  height: 100px;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }
  button {
    position: absolute;
    top: 2px;
    right: 2px;
    background: red;
    color: white;
    border: none;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    cursor: pointer;
    font-size: 12px;
    z-index: 10;
  }
`;

const UploadItem = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ProgressBar = styled.div`
  width: 100px;
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
  margin-left: 1rem;
  
  div {
    height: 100%;
    background: ${({ theme }) => theme.colors.secondary};
    width: ${props => props.$progress}%;
    transition: width 0.3s ease;
  }
`;

const HomeAdmin = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [uploadQueue, setUploadQueue] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [logoProgress, setLogoProgress] = useState(0);
  const [snippetProgress, setSnippetProgress] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/home');
      setData(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (section, field, value) => {
    setData(prev => {
      const newData = { ...prev };
      if (section === 'hero') {
        newData.hero[field] = value;
      } else {
        newData.snippets[section][field] = value;
      }
      return newData;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/home', data);
      setMsg('Changes saved successfully!');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoProgress(0);
    const timer = setInterval(() => {
      setLogoProgress(old => (old < 90 ? old + 10 : old));
    }, 100);

    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.upload('/home/header/logo', formData);
      clearInterval(timer);
      setLogoProgress(100);
      setData(prev => ({
        ...prev,
        header: res
      }));
      setTimeout(() => setLogoProgress(0), 1000);
    } catch (err) {
      clearInterval(timer);
      setLogoProgress(0);
      console.error('Logo upload failed', err);
      alert('Failed to upload logo');
    }
  };

  const handleHeroImageUpload = async (files) => {
    if (!files) return;
    const fileList = Array.isArray(files) ? files : [files];

    // Add files to queue
    const queueItems = fileList.map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name,
      progress: 0,
      status: 'pending'
    }));
    
    setUploadQueue(prev => [...prev, ...queueItems]);

    for (let i = 0; i < queueItems.length; i++) {
        const item = queueItems[i];
        const formData = new FormData();
        formData.append('image', item.file);

        // Update status to uploading
        setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'uploading' } : q));

        try {
          // Simulate progress since axios progress events might be tricky with current api wrapper
          // In a real app we'd pass onUploadProgress to axios
          const timer = setInterval(() => {
             setUploadQueue(prev => prev.map(q => {
                 if(q.id === item.id && q.progress < 90) {
                     return { ...q, progress: q.progress + 10 };
                 }
                 return q;
             }));
          }, 100);

          const res = await api.upload('/home/hero-image', formData);
          
          clearInterval(timer);
          
          // Update status to completed and remove from queue after a delay or immediately
          setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'completed', progress: 100 } : q));
          
          // Update data
          setData(prev => ({
            ...prev,
            hero: {
                ...prev.hero,
                images: res
            }
          }));
          
          // Remove from queue after short delay
          setTimeout(() => {
             setUploadQueue(prev => prev.filter(q => q.id !== item.id));
          }, 1000);

        } catch (err) {
          console.error('Upload failed', err);
          setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'error' } : q));
          alert(`Failed to upload ${item.name}`);
        }
    }
  };

  const handleDeleteHeroImage = async (index) => {
    if (!window.confirm('Delete this image?')) return;
    setDeleting(index);
    try {
      const res = await api.delete(`/home/hero-image/${index}`);
      // res is the updated images array
      setData(prev => ({
        ...prev,
        hero: {
            ...prev.hero,
            images: res
        }
      }));
    } catch (err) {
      console.error('Delete failed', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleSnippetImageUpload = async (snippetName, file) => {
    if (!file) return;
    setSnippetProgress(prev => ({ ...prev, [snippetName]: 0 }));
    const timer = setInterval(() => {
      setSnippetProgress(prev => ({
        ...prev,
        [snippetName]: (prev[snippetName] || 0) < 90 ? (prev[snippetName] || 0) + 10 : (prev[snippetName] || 0)
      }));
    }, 100);

    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.upload(`/home/snippet-image/${snippetName}`, formData);
      clearInterval(timer);
      setSnippetProgress(prev => ({ ...prev, [snippetName]: 100 }));
      
      setData(prev => ({
        ...prev,
        snippets: {
            ...prev.snippets,
            [snippetName]: res
        }
      }));
      
      setTimeout(() => {
        setSnippetProgress(prev => {
            const next = { ...prev };
            delete next[snippetName];
            return next;
        });
      }, 1000);
    } catch (err) {
      clearInterval(timer);
      setSnippetProgress(prev => ({ ...prev, [snippetName]: 0 }));
      console.error('Upload failed', err);
      alert('Failed to upload snippet image');
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!data) return <div>Error</div>;

  const { hero, snippets } = data;

  return (
    <Container>
      <h2>Manage Home Page</h2>
      {msg && <div style={{color:'green', marginBottom:'1rem'}}>{msg}</div>}
      <Button onClick={handleSave} style={{marginBottom:'2rem'}} disabled={saving}>
        {saving ? 'Saving...' : 'Save Text Changes'}
      </Button>

      <Card>
        <CardContent>
          <SectionHeader>Website Header</SectionHeader>
          <FormGroup>
             <label>Site Title</label>
             <Input 
                value={data.header?.title || ''} 
                onChange={(e) => setData({...data, header: {...data.header, title: e.target.value}})} 
             />
          </FormGroup>
          <FormGroup>
             <label>Logo</label>
             <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
                <div style={{flex:1}}>
                    <ImageUploader 
                        previewUrl={data.header?.logo} 
                        onImageSelect={handleLogoUpload} 
                    />
                </div>
                {logoProgress > 0 && (
                    <div style={{display:'flex', alignItems:'center', marginLeft:'1rem'}}>
                        <span style={{fontSize:'0.8rem', marginRight:'0.5rem'}}>{logoProgress}%</span>
                        <ProgressBar $progress={logoProgress} style={{margin:0}}><div /></ProgressBar>
                    </div>
                )}
             </div>
          </FormGroup>
        </CardContent>
      </Card>

      <Card style={{marginTop:'1rem'}}>
        <CardContent>
          <SectionHeader>Hero Section</SectionHeader>
          <FormGroup>
            <label>Hero Text</label>
            <Input 
              value={hero.text} 
              onChange={(e) => handleTextChange('hero', 'text', e.target.value)} 
            />
          </FormGroup>
          <FormGroup>
            <label>Slideshow Images (Max 4)</label>
            <ImageUploader onImageSelect={handleHeroImageUpload} multiple={true} />
            
            {/* Upload Queue Display */}
            {uploadQueue.length > 0 && (
                <div style={{marginTop: '1rem'}}>
                    {uploadQueue.map(item => (
                        <UploadItem key={item.id}>
                            <span style={{fontSize:'0.9rem'}}>{item.name}</span>
                            <div style={{display:'flex', alignItems:'center'}}>
                                <span style={{fontSize:'0.8rem', color:'#666'}}>{item.status === 'completed' ? 'Done' : item.status === 'error' ? 'Error' : `${item.progress}%`}</span>
                                <ProgressBar $progress={item.progress}><div /></ProgressBar>
                            </div>
                        </UploadItem>
                    ))}
                </div>
            )}

            <HeroImageGrid>
              {hero.images.map((img, idx) => (
                <Thumbnail key={idx}>
                  <img src={getImageUrl(img)} alt={`Slide ${idx}`} />
                  <button onClick={() => handleDeleteHeroImage(idx)}>x</button>
                </Thumbnail>
              ))}
            </HeroImageGrid>
          </FormGroup>
        </CardContent>
      </Card>

      <Card style={{marginTop:'1rem'}}>
        <CardContent>
            <SectionHeader>Footer Information</SectionHeader>
            <FormGroup>
                <label>Address</label>
                <Input 
                    value={data.footer?.address || ''} 
                    onChange={(e) => setData({...data, footer: {...data.footer, address: e.target.value}})} 
                />
            </FormGroup>
            <FormGroup>
                <label>Phone</label>
                <Input 
                    value={data.footer?.phone || ''} 
                    onChange={(e) => setData({...data, footer: {...data.footer, phone: e.target.value}})} 
                />
            </FormGroup>
            <FormGroup>
                <label>Email / Contact Info</label>
                <Input 
                    value={data.footer?.contactInfo || ''} 
                    onChange={(e) => setData({...data, footer: {...data.footer, contactInfo: e.target.value}})} 
                />
            </FormGroup>
        </CardContent>
      </Card>

      <div style={{marginTop: '2rem'}}>
        {['about', 'workshop', 'outreach', 'donate', 'contact'].map((key) => (
          <Card key={key} style={{marginBottom: '1rem'}}>
            <CardContent>
              <SectionHeader>{key.charAt(0).toUpperCase() + key.slice(1)} Snippet</SectionHeader>
              <FormGroup>
                <label>Title</label>
                <Input 
                  value={snippets[key].title} 
                  onChange={(e) => handleTextChange(key, 'title', e.target.value)} 
                />
              </FormGroup>
              <FormGroup>
                <label>Content</label>
                <TextArea 
                  value={snippets[key].content} 
                  onChange={(e) => handleTextChange(key, 'content', e.target.value)} 
                />
              </FormGroup>
              {key !== 'contact' && (
                <FormGroup>
                    <label>Image</label>
                    <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
                        <div style={{flex:1}}>
                            <ImageUploader 
                                previewUrl={snippets[key].image} 
                                onImageSelect={(file) => handleSnippetImageUpload(key, file)} 
                            />
                        </div>
                        {snippetProgress[key] > 0 && (
                            <div style={{display:'flex', alignItems:'center', marginLeft:'1rem'}}>
                                <span style={{fontSize:'0.8rem', marginRight:'0.5rem'}}>{snippetProgress[key]}%</span>
                                <ProgressBar $progress={snippetProgress[key]} style={{margin:0}}><div /></ProgressBar>
                            </div>
                        )}
                    </div>
                </FormGroup>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </Container>
  );
};

export default HomeAdmin;
